import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import {
  MessageSquare,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "../../../../components/ui/card";
import { Badge } from "../../../../components/ui/badge";
import { Skeleton } from "../../../../components/ui/skeleton";
import { Alert, AlertDescription } from "../../../../components/ui/alert";
import { Separator } from "../../../../components/ui/separator";
import { Button } from "../../../../components/ui/button";

import { getLeadCommsDetails } from "../../../../services/AdminApis";

// ── Constants ────────────────────────────────────────────────────────────────

const NOTIFICATION_TYPE_LABELS = {
  buyer_request_ack: "Request Acknowledgment",
  seller_purchase_alert: "Seller Purchase Alert",
};

const WHATSAPP_STATUS_LABELS = {
  accepted: "Accepted",
  sent: "Sent",
  delivered: "Delivered",
  read: "Read",
  failed: "Failed",
};

// ── Helpers ──────────────────────────────────────────────────────────────────

const formatTs = (ts) => (ts ? format(new Date(ts), "dd MMM yyyy, HH:mm") : null);
const formatTsShort = (ts) => (ts ? format(new Date(ts), "HH:mm") : null);

// ── Delivery status pill shown in the thread header ─────────────────────────

const DeliveryPill = ({ detail }) => {
  if (!detail?.whatsapp_status) return null;
  const s = detail.whatsapp_status.toLowerCase();
  const label = WHATSAPP_STATUS_LABELS[s] ?? detail.whatsapp_status;

  if (s === "read") {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-brand-growth-green">
        <CheckCircle2 className="h-3.5 w-3.5" />
        {label}
      </span>
    );
  }
  if (s === "delivered") {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-primary">
        <CheckCircle2 className="h-3.5 w-3.5" />
        {label}
      </span>
    );
  }
  if (s === "sent") {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground">
        <Clock className="h-3.5 w-3.5" />
        {label}
      </span>
    );
  }
  if (s === "failed") {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-destructive">
        <XCircle className="h-3.5 w-3.5" />
        {label}
      </span>
    );
  }
  return <span className="text-xs text-muted-foreground">{label}</span>;
};

// ── Keys to hide from the payload display (internal linking / sensitive) ─────

const PAYLOAD_HIDDEN_KEYS = new Set([
  "lead_id",
  "lead_purchase_id",
  "seller_profile_id",
]);

const PAYLOAD_LABELS = {
  buyer_name:           "Buyer Name",
  category_noun:        "Category",
  category_plural:      "Category (Plural)",
  seller_name:          "Seller Name",
  seller_business_name: "Business Name",
  seller_email:         "Seller Email",
  seller_phone:         "Seller Phone",
  template_variant:     "Template Variant",
  extra_link_1:         "Portfolio / Custom Link",
  extra_link_2:         "Social Link",
};

// ── Message payload details panel ────────────────────────────────────────────

const MessagePayload = ({ payload }) => {
  if (!payload) return null;

  const displayEntries = Object.entries(payload).filter(
    ([key, value]) => !PAYLOAD_HIDDEN_KEYS.has(key) && value !== null && value !== undefined && value !== ""
  );

  if (displayEntries.length === 0) return null;

  return (
    <div className="mt-2 rounded-lg border border-border bg-muted/30 px-3 py-2 text-xs">
      <p className="mb-1.5 font-semibold text-muted-foreground uppercase tracking-wide text-[10px]">
        Message Details
      </p>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
        {displayEntries.map(([key, value]) => (
          <React.Fragment key={key}>
            <dt className="text-muted-foreground whitespace-nowrap">
              {PAYLOAD_LABELS[key] ?? key}
            </dt>
            <dd className="font-medium text-foreground break-all">{String(value)}</dd>
          </React.Fragment>
        ))}
      </dl>
    </div>
  );
};

// ── Outbound chat bubble (right-aligned) ─────────────────────────────────────

const OutboundBubble = ({ message }) => {
  const detail = message.whatsapp_detail;

  // Compact delivery timeline shown below the bubble
  const timeline = detail
    ? [
        detail.sent_at && { label: "Sent", ts: detail.sent_at },
        detail.delivered_at && { label: "Delivered", ts: detail.delivered_at },
        detail.read_at && { label: "Read", ts: detail.read_at },
        detail.failed_at && { label: "Failed", ts: detail.failed_at, error: true },
      ].filter(Boolean)
    : [];

  return (
    <div className="flex justify-end">
      <div className="max-w-[80%] space-y-1">
        {/* Bubble */}
        <div className="rounded-2xl rounded-tr-sm bg-primary px-4 py-2.5 text-primary-foreground shadow-sm">
          <p className="text-xs font-semibold opacity-80">{message.sent_to}</p>
          {message.template_id && (
            <p className="mt-0.5 font-mono text-xs opacity-70">
              {message.template_id}
            </p>
          )}
        </div>

        {/* Delivery timestamps */}
        {timeline.length > 0 && (
          <div className="flex flex-wrap justify-end gap-x-3 gap-y-0.5 pr-1 text-[10px] text-muted-foreground">
            {timeline.map(({ label, ts, error }) => (
              <span
                key={label}
                className={`flex items-center gap-0.5 ${error ? "text-destructive" : ""}`}
              >
                {error ? (
                  <XCircle className="h-3 w-3" />
                ) : (
                  <Clock className="h-3 w-3" />
                )}
                {label}: {formatTsShort(ts)}
              </span>
            ))}
          </div>
        )}

        {/* Failure detail */}
        {detail?.failed_at && detail?.error_details && (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 px-2 py-1 text-[10px] text-destructive">
            {detail.error_code && (
              <span className="font-mono mr-1">[{detail.error_code}]</span>
            )}
            {detail.error_details}
          </div>
        )}

        {/* Message details payload */}
        <MessagePayload payload={message.payload} />
      </div>
    </div>
  );
};

// ── Inbound chat bubble (left-aligned) ───────────────────────────────────────

const InboundBubble = ({ reply }) => (
  <div className="flex justify-start">
    <div className="max-w-[80%] space-y-1">
      <div className="rounded-2xl rounded-tl-sm border border-border bg-accent/50 px-4 py-2.5 shadow-sm">
        <p className="text-xs font-semibold text-foreground">
          {reply.sender_profile_name || reply.sender_phone}
        </p>
        {reply.content && (
          <p className="mt-0.5 text-sm text-foreground">{reply.content}</p>
        )}
      </div>
      <p className="pl-1 text-[10px] text-muted-foreground">
        {formatTs(reply.received_at)}
      </p>
    </div>
  </div>
);

// ── Conversation thread card ──────────────────────────────────────────────────

const ConversationThread = ({ message }) => {
  const [expanded, setExpanded] = useState(true);
  const replyCount = message.inbound_replies?.length ?? 0;
  const notifLabel =
    NOTIFICATION_TYPE_LABELS[message.notification_type] ?? message.notification_type;

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      {/* Thread header */}
      <div
        className="flex cursor-pointer items-center gap-2 px-4 py-3 hover:bg-muted/30 transition-colors"
        onClick={() => setExpanded((v) => !v)}
        role="button"
        aria-expanded={expanded}
      >
        {/* WhatsApp pill */}
        <span className="inline-flex items-center rounded-full bg-[#25D366]/10 px-2 py-0.5 text-xs font-semibold text-[#25D366]">
          WhatsApp
        </span>

        <Badge variant="secondary" className="text-xs">
          {notifLabel}
        </Badge>

        <DeliveryPill detail={message.whatsapp_detail} />

        {replyCount > 0 && (
          <span className="ml-1 text-xs text-muted-foreground">
            {replyCount} {replyCount === 1 ? "reply" : "replies"}
          </span>
        )}

        <span className="ml-auto text-xs text-muted-foreground">
          {formatTs(message.created_at)}
        </span>

        {expanded ? (
          <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}
      </div>

      {/* Chat thread body */}
      {expanded && (
        <div className="space-y-3 border-t border-border bg-muted/10 px-4 py-4">
          {/* Outbound message */}
          <OutboundBubble message={message} />

          {/* Inbound replies interspersed after outbound */}
          {replyCount > 0 &&
            message.inbound_replies.map((reply, idx) => (
              <InboundBubble key={idx} reply={reply} />
            ))}
        </div>
      )}
    </div>
  );
};

// ── Loading skeleton ──────────────────────────────────────────────────────────

const LoadingSkeleton = () => (
  <div className="space-y-3">
    {[1, 2].map((i) => (
      <div key={i} className="rounded-xl border border-border p-4 space-y-3">
        <div className="flex gap-2">
          <Skeleton className="h-5 w-20" />
          <Skeleton className="h-5 w-36" />
          <Skeleton className="h-5 w-16 ml-auto" />
        </div>
        <div className="flex justify-end">
          <Skeleton className="h-14 w-56 rounded-2xl" />
        </div>
      </div>
    ))}
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────

const LeadCommsSection = ({ leadId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [comms, setComms] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch only on first expand
  useEffect(() => {
    if (!isOpen || hasFetched || !leadId) return;

    const fetchComms = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await getLeadCommsDetails(leadId);
        setComms(data);
      } catch (err) {
        console.error("LeadCommsSection: failed to fetch comms", err);
        setError(err);
      } finally {
        setIsLoading(false);
        setHasFetched(true);
      }
    };

    fetchComms();
  }, [isOpen, hasFetched, leadId]);

  const messages = comms?.messages ?? [];

  return (
    <Card>
      {/* Clickable header toggles the section */}
      <CardHeader
        className="cursor-pointer select-none hover:bg-muted/30 transition-colors rounded-t-xl"
        onClick={() => setIsOpen((v) => !v)}
        role="button"
        aria-expanded={isOpen}
      >
        <CardTitle className="flex items-center gap-2 text-base">
          <MessageSquare className="h-5 w-5 text-primary" />
          Communications
          {hasFetched && !error && (
            <Badge variant="outline" className="text-xs font-normal">
              {messages.length} {messages.length === 1 ? "conversation" : "conversations"}
            </Badge>
          )}
          <span className="ml-auto">
            {isOpen ? (
              <ChevronUp className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            )}
          </span>
        </CardTitle>
        {!isOpen && (
          <p className="text-xs text-muted-foreground mt-0.5">
            Click to view WhatsApp messages sent for this lead
          </p>
        )}
      </CardHeader>

      {isOpen && (
        <CardContent className="pt-0 space-y-3">
          {isLoading && <LoadingSkeleton />}

          {!isLoading && error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                {error.response?.data?.detail ||
                  error.message ||
                  "Failed to load communications."}
              </AlertDescription>
            </Alert>
          )}

          {!isLoading && !error && hasFetched && messages.length === 0 && (
            <p className="py-4 text-center text-sm text-muted-foreground">
              No communications recorded for this lead.
            </p>
          )}

          {!isLoading &&
            !error &&
            messages.map((message) => (
              <ConversationThread key={message.comms_log_id} message={message} />
            ))}
        </CardContent>
      )}
    </Card>
  );
};

export default LeadCommsSection;
